/**
 * Copyright ©2026 Drivepoint
 */

export type FirebaseSignInFlow = "popup" | "redirect";

type SignInFlowEnvironment = {
  userAgent: string;
  maxTouchPoints: number;
};

function readSignInFlowEnvironment(): SignInFlowEnvironment {
  if (typeof navigator === "undefined") {
    return {userAgent: "", maxTouchPoints: 0};
  }
  return {
    userAgent: navigator.userAgent || "",
    maxTouchPoints: navigator.maxTouchPoints || 0
  };
}

function isIOS(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return true;
  }
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
}

function isEmbeddedWebView(userAgent: string): boolean {
  if (/; wv\)/i.test(userAgent)) {
    return true;
  }
  if (/WebView/i.test(userAgent)) {
    return true;
  }
  return /(FBAN|FBAV|Instagram|Line\/|LinkedInApp|Twitter|GSA\/)/i.test(userAgent);
}

// Chrome (especially Android) infinite-loops on FirebaseUI redirect because the
// cross-origin auth helper is treated as a bounce / partitioned third party.
// Popups work on Chromium with a user gesture. iOS and embedded WebViews block
// or break popups, so those keep the redirect flow.
export function getFirebaseSignInFlow(
  userAgent?: string,
  maxTouchPoints?: number
): FirebaseSignInFlow {
  const environment = readSignInFlowEnvironment();
  const resolvedUserAgent = userAgent ?? environment.userAgent;
  const resolvedMaxTouchPoints = maxTouchPoints ?? environment.maxTouchPoints;
  if (isEmbeddedWebView(resolvedUserAgent) || isIOS(resolvedUserAgent, resolvedMaxTouchPoints)) {
    return "redirect";
  }
  return "popup";
}
